import {
	BasiliskJobDetail,
	BasiliskResult,
	BasiliskReview,
	BasiliskReviewPerson,
	CompleteBasiliskReviewPayload,
	normalizeManticoreSeverity,
	RiskClassification,
} from 'modules/basilisk/basilisk.model';
import { BASILISK_RISK_CONFIG } from 'modules/basilisk/basilisk.const';
import { JobStatusLabel } from 'services/jobs/jobs.model';
import { MockHttpError, MockRoute, mockRoute, nowIso } from '../mock-api';
import { jobView } from '../jobs/jobs.const';
import { buildCurrentUser } from '../session/session.mock';
import { MOCK_PEOPLE, MOCK_SAMPLE_PDF_URL } from '../shared/mock-context.const';
import { currentJobStatus, findJobEntry, setJobStatus } from '../shared/mock-job-state';
import { MockJobRosterEntry } from '../shared/mock-jobs-roster.const';
import {
	BASILISK_DEFAULT_SCENARIOS,
	BASILISK_FAILURE_MESSAGE,
	BASILISK_ROSTER_REVIEWS,
	BASILISK_ROSTER_SCENARIOS,
	BasiliskScenario,
} from './basilisk.const';

const HOUR_MS = 60 * 60 * 1000;
const QUEUE_TIME_MS = 4_000;
const reviews = new Map<string, BasiliskReview>();

// ── Helpers ──────────────────────────────────────────────────────────────────

function hashSeed(text: string): number {
	let hash = 2166136261;
	for (let index = 0; index < text.length; index++) {
		hash ^= text.charCodeAt(index);
		hash = Math.imul(hash, 16777619);
	}
	return hash >>> 0;
}

/** SHA-256 fictício, estável por job */
function fakeSha256(jobId: string): string {
	let state = hashSeed(jobId);
	let digest = '';
	while (digest.length < 64) {
		state = Math.imul(state ^ (state >>> 15), 2246822507) >>> 0;
		digest += state.toString(16).padStart(8, '0');
	}
	return digest.slice(0, 64);
}

function scenarioOf(jobId: string): BasiliskScenario {
	return (
		BASILISK_ROSTER_SCENARIOS[jobId] ??
		BASILISK_DEFAULT_SCENARIOS[hashSeed(jobId) % BASILISK_DEFAULT_SCENARIOS.length]
	);
}

/** Mesma data de criação da caixa de entrada (mocks/jobs), para lista e detalhe baterem */
function createdAtMs(entry: MockJobRosterEntry): number {
	return jobView(entry).createdAtMs;
}

function toIso(ms: number): string {
	return new Date(ms).toISOString();
}

function currentReviewPerson(): BasiliskReviewPerson {
	try {
		const user = buildCurrentUser();
		return { id: user.id, name: user.name, email: user.email, image: user.image };
	} catch {
		return MOCK_PEOPLE.admin;
	}
}

function findBasiliskEntry(jobId: string): MockJobRosterEntry {
	const entry = findJobEntry(jobId);
	if (!entry || entry.product !== 'basilisk') {
		throw new MockHttpError(
			404,
			'Análise não encontrada',
			`Nenhuma análise do Basilisk com id ${jobId}.`,
		);
	}
	return entry;
}

function statusOf(entry: MockJobRosterEntry): JobStatusLabel {
	return currentJobStatus(entry.id) ?? entry.status;
}

function hasAnalysis(status: JobStatusLabel): boolean {
	return status === 'Aguardando revisão' || status === 'Em revisão' || status === 'Finalizado';
}

/** Revisão do job — semeada a partir do roster na primeira leitura */
function reviewOf(entry: MockJobRosterEntry): BasiliskReview | null {
	const stored = reviews.get(entry.id);
	if (stored) return stored;

	const created = createdAtMs(entry);
	if (entry.id === 'job-bs-002') {
		const seed = BASILISK_ROSTER_REVIEWS['job-bs-002'];
		const review: BasiliskReview = {
			openedAt: toIso(created + seed.openedHoursAfterCreation * HOUR_MS),
			openedBy: seed.openedBy,
			description: null,
			severity: null,
			completedAt: null,
			completedBy: null,
		};
		reviews.set(entry.id, review);
		return review;
	}
	if (entry.id === 'job-bs-003') {
		const seed = BASILISK_ROSTER_REVIEWS['job-bs-003'];
		const review: BasiliskReview = {
			openedAt: toIso(created + seed.openedHoursAfterCreation * HOUR_MS),
			openedBy: seed.openedBy,
			description: seed.description,
			severity: seed.severity,
			completedAt: toIso(created + seed.completedHoursAfterCreation * HOUR_MS),
			completedBy: seed.completedBy,
		};
		reviews.set(entry.id, review);
		return review;
	}
	return null;
}

function buildResult(entry: MockJobRosterEntry, review: BasiliskReview | null): BasiliskResult {
	const scenario = scenarioOf(entry.id);
	const analysedAt = createdAtMs(entry) + QUEUE_TIME_MS + scenario.processingTimeMs;
	return {
		manticore: {
			filename: entry.description,
			hash: fakeSha256(entry.id),
			ts: toIso(analysedAt),
			bytes: scenario.bytes,
			chars: scenario.chars,
			severity: scenario.severity,
			advise: scenario.advise,
			camada4: review?.completedAt ? review.description : null,
			findings: scenario.findings,
		},
		aiReview: scenario.aiReview,
	};
}

function buildJobDetail(jobId: string): BasiliskJobDetail {
	const entry = findBasiliskEntry(jobId);
	const status = statusOf(entry);
	const scenario = scenarioOf(entry.id);
	const review = hasAnalysis(status) ? reviewOf(entry) : null;
	const created = createdAtMs(entry);
	const started = status === 'Aguardando' ? null : created + QUEUE_TIME_MS;
	const analysed = created + QUEUE_TIME_MS + scenario.processingTimeMs;

	let finished: number | null = null;
	if (status === 'Falha') finished = created + QUEUE_TIME_MS + 9_000;
	else if (status === 'Finalizado')
		finished = review?.completedAt ? Date.parse(review.completedAt) : analysed;
	else if (hasAnalysis(status)) finished = analysed;

	const processingTimeMs = started !== null && finished !== null ? finished - started : null;
	const person = MOCK_PEOPLE[entry.createdBy];

	return {
		id: entry.id,
		status,
		source: entry.source,
		description: null,
		createdAt: toIso(created),
		startedAt: started !== null ? toIso(started) : null,
		finishedAt: finished !== null ? toIso(finished) : null,
		createdBy: { id: person.id, name: person.name, email: person.email },
		isOverLimit: false,
		pagesProcessed: hasAnalysis(status) ? scenario.pageCount : null,
		documents: [
			{
				fileId: `file-${entry.id}`,
				filename: entry.description,
				fileTypeName: scenario.fileTypeName,
				mimeType: 'application/pdf',
				sizeBytes: scenario.bytes,
				pageCount: scenario.pageCount,
				uploadedAt: toIso(created),
				downloadUrl: MOCK_SAMPLE_PDF_URL,
			},
		],
		setupSnapshot: null,
		timings: {
			queueTimeMs: started !== null ? QUEUE_TIME_MS : null,
			processingTimeMs,
			totalTimeMs: processingTimeMs !== null ? processingTimeMs + QUEUE_TIME_MS : null,
		},
		resultAvailable: hasAnalysis(status),
		result: hasAnalysis(status) ? buildResult(entry, review) : null,
		failure:
			status === 'Falha'
				? {
						...BASILISK_FAILURE_MESSAGE,
						occurredAt: toIso(finished ?? created),
						retryCount: 3,
						retryExhausted: true,
					}
				: null,
		billing: hasAnalysis(status)
			? {
					appliedTier: null,
					chargedValueBrl: 4.9 * scenario.pageCount,
					chargedValueUsd: 0.89 * scenario.pageCount,
				}
			: null,
		_links: {
			self: `/jobs/${entry.id}`,
			status: `/jobs/${entry.id}/status`,
			result: `/jobs/${entry.id}/result`,
			resultDownloads: {
				xlsx: `/jobs/${entry.id}/result/download?format=xlsx`,
				json: `/jobs/${entry.id}/result/download?format=json`,
				xml: `/jobs/${entry.id}/result/download?format=xml`,
			},
		},
		review,
	};
}

function openReview(jobId: string): BasiliskJobDetail {
	const entry = findBasiliskEntry(jobId);
	if (statusOf(entry) !== 'Aguardando revisão') {
		throw new MockHttpError(
			409,
			'Revisão indisponível',
			'Só é possível abrir a revisão de análises aguardando revisão.',
		);
	}
	reviews.set(entry.id, {
		openedAt: nowIso(),
		openedBy: currentReviewPerson(),
		description: null,
		severity: null,
		completedAt: null,
		completedBy: null,
	});
	setJobStatus(entry.id, 'Em revisão');
	return buildJobDetail(entry.id);
}

function completeReview(jobId: string, payload: CompleteBasiliskReviewPayload): BasiliskJobDetail {
	const entry = findBasiliskEntry(jobId);
	const review = reviewOf(entry);
	if (statusOf(entry) !== 'Em revisão' || !review || review.completedAt) {
		throw new MockHttpError(
			409,
			'Revisão indisponível',
			'A revisão precisa estar aberta para ser concluída.',
		);
	}
	if (
		!payload.description?.trim() ||
		!Object.values(RiskClassification).includes(payload.severity)
	) {
		throw new MockHttpError(
			400,
			'Dados inválidos',
			'Informe o parecer e a classificação da revisão.',
		);
	}
	reviews.set(entry.id, {
		...review,
		description: payload.description.trim(),
		severity: payload.severity,
		completedAt: nowIso(),
		completedBy: currentReviewPerson(),
	});
	setJobStatus(entry.id, 'Finalizado');
	return buildJobDetail(entry.id);
}

// ── Laudo em PDF (placeholder gerado na hora) ────────────────────────────────

function toPdfText(text: string): string {
	return text
		.replace(/[—–]/g, '-')
		.replace(/[“”]/g, '"')
		.replace(/[^\x20-\xFF]/g, '')
		.replace(/[\\()]/g, (character) => `\\${character}`);
}

function wrapLine(text: string, width = 92): string[] {
	const lines: string[] = [];
	let current = '';
	for (const word of text.split(' ')) {
		if ((current + ' ' + word).trim().length > width) {
			lines.push(current);
			current = word;
		} else {
			current = (current + ' ' + word).trim();
		}
	}
	if (current) lines.push(current);
	return lines;
}

function reportLines(job: BasiliskJobDetail): string[] {
	const manticore = job.result?.manticore;
	const lines = [
		'LAUDO BASILISK - ANALISE FORENSE DE DOCUMENTO',
		'',
		`Job: ${job.id}`,
		`Arquivo: ${manticore?.filename ?? job.documents[0]?.filename ?? '-'}`,
		`Hash (SHA-256): ${manticore?.hash ?? '-'}`,
		`Data da análise: ${manticore ? new Date(manticore.ts).toLocaleString('pt-BR') : '-'}`,
		'',
	];
	if (manticore) {
		const severity = BASILISK_RISK_CONFIG[normalizeManticoreSeverity(manticore.severity)];
		lines.push(`Classificação da análise: ${severity.label}`);
		lines.push(...wrapLine(`Recomendação: ${manticore.advise}`));
		lines.push('', 'Achados:');
		for (const finding of manticore.findings) {
			lines.push(
				...wrapLine(
					`[Camada ${finding.layer}] ${finding.id} - ${finding.category}: ${finding.description}`,
				),
			);
		}
	}
	const review = job.review;
	lines.push('', 'Revisão humana (Camada 4):');
	if (review?.completedAt && review.severity) {
		lines.push(`Classificação do revisor: ${BASILISK_RISK_CONFIG[review.severity].label}`);
		lines.push(...wrapLine(`Parecer: ${review.description ?? ''}`));
		lines.push(
			`Revisado por ${review.completedBy?.name ?? '-'} em ${new Date(review.completedAt).toLocaleString('pt-BR')}`,
		);
	} else {
		lines.push('Revisão pendente.');
	}
	return lines;
}

/** PDF mínimo (1 página, Helvetica/WinAnsi) com o resumo do laudo */
function buildReportPdf(job: BasiliskJobDetail): Blob {
	const textOps = reportLines(job)
		.map((line, index) => `${index === 0 ? '' : 'T* '}(${toPdfText(line)}) Tj`)
		.join('\n');
	const content = `BT\n/F1 10 Tf\n14 TL\n50 800 Td\n${textOps}\nET`;
	const objects = [
		'<< /Type /Catalog /Pages 2 0 R >>',
		'<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
		'<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 5 0 R >> >> /Contents 4 0 R >>',
		`<< /Length ${content.length} >>\nstream\n${content}\nendstream`,
		'<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>',
	];
	let pdf = '%PDF-1.4\n';
	const offsets: number[] = [];
	objects.forEach((object, index) => {
		offsets.push(pdf.length);
		pdf += `${index + 1} 0 obj\n${object}\nendobj\n`;
	});
	const xrefOffset = pdf.length;
	pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
	pdf += offsets.map((offset) => `${String(offset).padStart(10, '0')} 00000 n \n`).join('');
	pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;

	// Todos os caracteres estão em Latin-1: 1 caractere = 1 byte (offsets do xref corretos)
	const bytes = Uint8Array.from(pdf, (character) => character.charCodeAt(0));
	return new Blob([bytes], { type: 'application/pdf' });
}

function downloadReport(jobId: string): Blob {
	const job = buildJobDetail(jobId);
	if (!hasAnalysis(job.status)) {
		throw new MockHttpError(
			409,
			'Laudo indisponível',
			'O laudo só fica disponível após a análise do Manticore.',
		);
	}
	return buildReportPdf(job);
}

// ── Rotas ────────────────────────────────────────────────────────────────────

export const BASILISK_MOCK_ROUTES: MockRoute[] = [
	mockRoute('GET', '/jobs/:jobId/basilisk/review', ({ params }) => buildJobDetail(params['jobId'])),
	mockRoute<CompleteBasiliskReviewPayload>(
		'POST',
		'/jobs/:jobId/basilisk/review/complete',
		({ params, body }) => completeReview(params['jobId'], body),
	),
	mockRoute('POST', '/jobs/:jobId/basilisk/review', ({ params }) => openReview(params['jobId'])),
	mockRoute('GET', '/jobs/:jobId/basilisk/report', ({ params }) => downloadReport(params['jobId'])),
];
